import { ApiError, UNAUTHORIZED_EVENT } from '../api.ts';
import type { CompanionEvent, CompanionStatus, Proposal, Values } from './types.ts';

const HEADERS = { 'content-type': 'application/json', 'x-studio-request': '1' };

async function call<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/admin/companion${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'same-origin',
    headers: body === undefined ? { 'x-studio-request': '1' } : HEADERS,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new ApiError(response.status, String(payload['error'] ?? 'error'), String(payload['message'] ?? response.statusText), (payload['details'] as Record<string, unknown>) ?? {});
  }
  return payload as T;
}

export const companionApi = {
  status: () => call<CompanionStatus>('/status'),
  proposal: async (ref: string) => (await call<{ proposal: Proposal }>(`/proposals/${ref}`)).proposal,
  confirm: async (ref: string, body: { rows: { id: string; included: boolean; proposed?: Values }[] }) => (await call<{ proposal: Proposal }>(`/proposals/${ref}/confirm`, body)).proposal,
  discard: async (ref: string) => (await call<{ proposal: Proposal }>(`/proposals/${ref}/discard`, {})).proposal,
  undo: async (ref: string) => (await call<{ proposal: Proposal }>(`/proposals/${ref}/undo`, {})).proposal,
  undoable: async () => (await call<{ proposal: Proposal | null }>('/undoable')).proposal,
  redo: (ref: string, rows: string[], note: string, conversation: string) => call<{ proposal: Proposal; text: string }>(`/proposals/${ref}/redo`, { rows, note, conversation }),

  /** Sends a prompt and calls onEvent for every streamed event (NDJSON), as it arrives. */
  async chat(
    body: { message: string; history: { role: 'user' | 'assistant'; text: string }[]; conversation: string; page: string },
    onEvent: (event: CompanionEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const response = await fetch('/admin/companion/chat', { method: 'POST', credentials: 'same-origin', headers: HEADERS, body: JSON.stringify(body), ...(signal ? { signal } : {}) });
    if (!response.ok || !response.body) {
      const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      if (response.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
      throw new ApiError(response.status, String(payload['error'] ?? 'error'), String(payload['message'] ?? response.statusText));
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (value) buffer += decoder.decode(value, { stream: true });
      let newline = buffer.indexOf('\n');
      while (newline >= 0) {
        const line = buffer.slice(0, newline).trim();
        buffer = buffer.slice(newline + 1);
        if (line) onEvent(JSON.parse(line) as CompanionEvent);
        newline = buffer.indexOf('\n');
      }
      if (done) break;
    }
  },
};

/** A random id per conversation (groups audit rows; carries nothing about the person). */
export function newConversationId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return `cv_${[...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')}`;
}
